// src/api.js

import axios from "axios";

// Pull in the backend URL from your env-var (set in Vercel)
const API_URL = process.env.REACT_APP_API_URL;

if (!API_URL) {
  console.error("❌ REACT_APP_API_URL is not defined. Set it in Vercel env vars.");
  throw new Error("REACT_APP_API_URL is required but not defined.");
}

// Create an axios instance whose baseURL points at your live Vercel backend
const API = axios.create({
  baseURL: `${API_URL}/api`,
  // Admin sync calls can take longer on cold starts; give generous default.
  timeout: 60000,
});

// ✅ Add token to every request if available
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const url = String(config.url || "");
  const isAdminSync =
    url.includes("/admin/") &&
    (url.includes("sync") || url.includes("world-cup"));
  if (isAdminSync) {
    config.timeout = url.includes("sync-full") ? 180000 : 120000;
  }

  return config;
});

// Handle 401 errors globally
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401s globally
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== '/auth' && window.location.pathname !== '/') {
        window.location.href = '/auth';
      }
      return Promise.reject(error);
    }

    // One-time retry on timeout/network aborts (with longer delay for cold starts)
    const cfg = error.config || {};
    const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    if (isTimeout && !cfg.__retriedOnce) {
      cfg.__retriedOnce = true;
      // Wait longer for server to wake up (2 seconds)
      await new Promise((r) => setTimeout(r, 2000));
      return API(cfg);
    }

    return Promise.reject(error);
  }
);

// ——— Existing named exports ———

/** Fetch fixtures for a given gameweek */
export async function fetchGameweekFixtures(gameweekNumber) {
  try {
    const response = await API.get(`/gameweeks/${gameweekNumber}/fixtures`);
    return response.data;
  } catch (error) {
    console.error("Error fetching fixtures:", error);
    return [];
  }
}

/** Submit a user’s prediction for a fixture */
export async function submitUserPrediction(fixtureId, homeScore, awayScore) {
  try {
    const response = await API.post("/predictions/submit", {
      predictions: [
        {
          fixture_id: fixtureId,
          home_score: Number.isFinite(+homeScore) ? +homeScore : 0,
          away_score: Number.isFinite(+awayScore) ? +awayScore : 0,
        },
      ],
    });
    return response.data;
  } catch (error) {
    console.error("Error submitting prediction:", error);
    return null;
  }
}

/** Fetch the leaderboard data */
export async function fetchLeaderboard(season) {
  try {
    const response = await API.get("/leaderboard", {
      params: season ? { season } : undefined,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
    return [];
  }
}

// ——— Featured Match methods ———

/** Fetch the current Featured Match (admin) */
export function getFeaturedMatch() {
  return API.get("/admin/featured-match").then((res) => res.data);
}

/** Update the Featured Match (admin) */
export function updateFeaturedMatch({ title, infoLine, description, imageUrl }) {
  return API.patch("/admin/featured-match", {
    title,
    infoLine,
    description,
    imageUrl,
  }).then((res) => res.data);
}

// attach to the default export so FeaturedMatch.js can call API.getFeaturedMatch(...)
API.getFeaturedMatch = getFeaturedMatch;
API.updateFeaturedMatch = updateFeaturedMatch;

// ——— Default export (axios instance) ———
export default API;
